import { directoryAt, opfsRoot } from "../src/opfs.js";
import type { DeliveryPorts, TakeBytes } from "./deliver.js";

/**
 * The browser half of delivery: reading the capture back out of origin-private
 * storage, and the two ways a page can hand a file to the person at the keyboard.
 *
 * Separated from `deliver.ts` so that every *decision* about a take stays
 * testable in Node and only the platform calls live here. There is nothing in
 * this file worth a test that a browser would not have to run anyway.
 */

/**
 * The save dialog, when this browser has one.
 *
 * Chromium's, and the spec says as much: the supported browsers are desktop
 * Chromium-based ones, and this is the difference between "the take is where I
 * put it" and "the take is in Downloads". Undefined elsewhere, which is what
 * makes {@link deliverTake} fall through to the download.
 */
interface SaveFilePicker {
  (options: { suggestedName?: string }): Promise<{
    createWritable(): Promise<{
      write(data: ArrayBuffer): Promise<void>;
      close(): Promise<void>;
    }>;
  }>;
}

function savePicker(): SaveFilePicker | undefined {
  const picker = (window as unknown as { showSaveFilePicker?: SaveFilePicker })
    .showSaveFilePicker;
  return typeof picker === "function" ? picker : undefined;
}

export function browserDelivery(): DeliveryPorts {
  const picker = savePicker();
  return {
    read: readFromStorage,
    ...(picker
      ? {
          // Only the name is offered. A `types` filter would mean deciding the
          // container a second time, from the extension, when the name already
          // carries it and `containerExtension` in the core already owns the
          // question — and a filter that disagreed with the name is a dialog
          // that refuses to save the file it suggested.
          save: async (suggestedName, bytes): Promise<void> => {
            const handle = await picker({ suggestedName });
            const writable = await handle.createWritable();
            await writable.write(bytes);
            await writable.close();
          },
        }
      : {}),
    download: (suggestedName, bytes) => download(suggestedName, new Blob([bytes])),
  };
}

/**
 * The capture, out of origin-private storage.
 *
 * The path is slash-separated because it crossed a `postMessage`; the API takes
 * names, so it is split back into them here. The worker has already closed its
 * handle by this point — a sync access handle is an exclusive lock, so it has to
 * have.
 */
async function readFromStorage(path: string): Promise<TakeBytes> {
  const parts = path.split("/").filter(Boolean);
  const name = parts.pop();
  if (name === undefined) throw new Error(`cuekards: not a path: '${path}'`);
  const directory = await directoryAt(await opfsRoot(), parts);
  // The read side of the handle, which is `File`-shaped and available on the
  // window as well as in a worker — unlike the synchronous write handle, which
  // is why the two halves live where they do.
  const file = await (await directory.getFileHandle(name)).getFile();
  return file.arrayBuffer();
}

/**
 * The floor: hand a file to the browser's downloads.
 *
 * Needs no user gesture, which is what makes it the right answer when the save
 * dialog is refused for want of one — the alternative there is losing the take.
 * The object URL is revoked a minute later rather than immediately, because
 * revoking it before the browser has started reading it cancels the download.
 *
 * Exported because a take is not the only thing a tab hands over: the settings
 * file (web ticket 18) leaves by exactly this route. One copy of the anchor
 * dance, so that a browser quirk found through one of them is fixed for both.
 */
export function download(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
